import { TENANT_DOMAIN } from '@/constants'

const DOMAIN_GROUPS: Record<string, string[]> = {
  employment: ['candidate', 'employer', 'recruiter', 'interviewer', 'platform', 'developer'],
  travel: ['traveler', 'agent', 'platform'],
  health: ['patient', 'practitioner', 'platform'],
  agency: ['client', 'engineer', 'principal', 'platform'],
  recruitment: ['recruiter', 'interviewer', 'platform', 'developer'],
  calls: ['operator', 'platform'],
}

const PRIVILEGED_GROUPS = new Set(['platform', 'developer', 'operator'])

/** Internal seats hidden from platform admin UI (filters and badges). Platform group stays visible. */
const PLATFORM_UI_HIDDEN_GROUPS = new Set(['developer'])

const GROUP_LABELS: Record<string, { label: string; description: string }> = {
  platform: { label: 'Platform', description: 'Platform operator (client stakeholder)' },
  candidate: { label: 'Candidate', description: 'Can apply for jobs and view applications' },
  employer: { label: 'Employer', description: 'Can post jobs and manage applications' },
  recruiter: { label: 'Recruiter', description: 'Can search candidates and manage briefs' },
  interviewer: { label: 'Interviewer', description: 'Read-only interview workflow access' },
  patient: { label: 'Patient', description: 'Health companion end-user' },
  practitioner: { label: 'Practitioner', description: 'Health practitioner or coach' },
  traveler: { label: 'Traveler', description: 'Travel customer' },
  agent: { label: 'Agent', description: 'Travel agent or operator' },
  client: { label: 'Client', description: 'Agency client partner' },
  engineer: { label: 'Engineer', description: 'Agency engineer' },
  principal: { label: 'Principal', description: 'Agency principal' },
  operator: { label: 'Call operator', description: 'Call records and evaluations' },
}

function getTenantGroupNames(): string[] {
  return DOMAIN_GROUPS[TENANT_DOMAIN] ?? DOMAIN_GROUPS.employment
}

export function formatPlatformGroupLabel(groupName: string): string {
  return GROUP_LABELS[groupName]?.label ?? groupName
}

/** Groups shown on user rows for platform admins — developer is omitted; platform stays. */
export function getPlatformVisibleGroups(groups: string[]): string[] {
  return groups.filter((name) => !PLATFORM_UI_HIDDEN_GROUPS.has(name))
}

/** Tenant-scoped group filter options for admin user lists (no developer, no cross-tenant roles). */
export function getAdminUserGroupFilterOptions(): Array<{ value: string; label: string }> {
  const groups = getTenantGroupNames().filter((name) => !PLATFORM_UI_HIDDEN_GROUPS.has(name))
  return [
    { value: 'all', label: 'All groups' },
    ...groups.map((name) => ({
      value: name,
      label: formatPlatformGroupLabel(name),
    })),
  ]
}

/**
 * Groups a platform operator may assign for the active tenant. Front-facing roles always;
 * `platform` only on another user's account (the API keeps an operator's own seat).
 */
export function getPlatformAssignableGroups(
  { includePlatform = false }: { includePlatform?: boolean } = {},
): Array<{ name: string; label: string; description: string }> {
  return getTenantGroupNames()
    .filter((name) => !PRIVILEGED_GROUPS.has(name) || (includePlatform && name === 'platform'))
    .map((name) => ({
      name,
      label: formatPlatformGroupLabel(name),
      description: GROUP_LABELS[name]?.description ?? '',
    }))
}
